import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Sparkles, Play, RotateCcw, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

export const ExplodePanel = ({
  isActive,
  explodeAmount,
  onExplodeChange,
  onAnimatedExplode,
  parts,
  selectedPart,
  onPartSelect
}) => {
  const [isAutoExploding, setIsAutoExploding] = useState(false);
  const [localAmount, setLocalAmount] = useState(explodeAmount);
  
  // Sync local amount with prop
  useEffect(() => {
    setLocalAmount(explodeAmount);
  }, [explodeAmount]);
  
  // Auto explode animation
  const handleAutoExplode = () => {
    if (isAutoExploding) return;
    setIsAutoExploding(true);
    
    // Call the animated explode function if available
    if (onAnimatedExplode) {
      onAnimatedExplode(1, 2000); // Full explode over 2 seconds
    } else {
      // Fallback to manual animation
      const startTime = Date.now();
      const duration = 2000;
      const startVal = localAmount;
      const targetVal = 1;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const newVal = startVal + (targetVal - startVal) * eased;
        
        setLocalAmount(newVal);
        onExplodeChange(newVal);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAutoExploding(false);
        }
      };
      animate();
    }
    
    setTimeout(() => setIsAutoExploding(false), 2100);
  };
  
  // Quick explode buttons
  const quickAmounts = [
    { label: 'Collapse', value: 0, icon: RotateCcw },
    { label: '25%', value: 0.25 },
    { label: '50%', value: 0.5 },
    { label: '100%', value: 1, icon: Sparkles }
  ];
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="absolute top-20 left-4 w-72 bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl overflow-hidden z-30"
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          data-testid="explode-panel"
        >
          {/* Header */}
          <div className="p-4 border-b border-border/30 bg-gradient-to-r from-cyan-500/10 to-purple-500/10">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                <Layers className="w-4 h-4 text-white" />
              </div>
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                Exploded View
              </span>
            </h3>
          </div>
          
          <div className="p-4 space-y-5">
            {/* Premium Progress Bar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">Explosion Level</span>
                <motion.span 
                  className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent"
                  key={Math.round(localAmount * 100)}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                >
                  {Math.round(localAmount * 100)}%
                </motion.span>
              </div>
              
              {/* Custom progress bar */}
              <div className="relative h-3 bg-secondary/50 rounded-full overflow-hidden">
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20" />
                
                {/* Progress fill */}
                <motion.div 
                  className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-cyan-500 to-purple-500"
                  style={{ width: `${localAmount * 100}%` }}
                  initial={false}
                  animate={{ width: `${localAmount * 100}%` }}
                  transition={{ type: 'spring', damping: 20, stiffness: 200 }}
                />
                
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
                
                {/* Animated particles when exploding */}
                {localAmount > 0 && localAmount < 1 && (
                  <motion.div
                    className="absolute top-0 h-full w-4 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                    style={{ left: `${localAmount * 100 - 2}%` }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
              </div>
              
              {/* Increment buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8"
                  onClick={() => onExplodeChange(Math.max(0, localAmount - 0.1))}
                  disabled={localAmount <= 0}
                >
                  <ChevronDown className="w-4 h-4 mr-1" />
                  -10%
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8"
                  onClick={() => onExplodeChange(Math.min(1, localAmount + 0.1))}
                  disabled={localAmount >= 1}
                >
                  <ChevronUp className="w-4 h-4 mr-1" />
                  +10%
                </Button>
              </div>
            </div>
            
            {/* Quick buttons */}
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map(({ label, value, icon: Icon }) => (
                <motion.button
                  key={label}
                  onClick={() => onExplodeChange(value)}
                  className={`relative px-2 py-2.5 rounded-xl text-xs font-medium transition-all overflow-hidden ${
                    Math.abs(localAmount - value) < 0.05
                      ? 'bg-gradient-to-br from-cyan-500 to-purple-500 text-white shadow-lg shadow-cyan-500/25'
                      : 'bg-secondary/50 hover:bg-secondary text-foreground border border-border/50'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {Icon && <Icon className="w-3 h-3 mx-auto mb-0.5" />}
                  {label}
                </motion.button>
              ))}
            </div>
            
            {/* Auto Explode Button */}
            <motion.button
              onClick={handleAutoExplode}
              disabled={isAutoExploding || localAmount >= 1}
              className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                isAutoExploding
                  ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white'
                  : 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 hover:from-cyan-500/30 hover:to-purple-500/30 border border-cyan-500/30 text-cyan-400'
              }`}
              whileHover={{ scale: isAutoExploding ? 1 : 1.02 }}
              whileTap={{ scale: isAutoExploding ? 1 : 0.98 }}
            >
              {isAutoExploding ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkles className="w-4 h-4" />
                  </motion.div>
                  Exploding...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Auto Explode (2s)
                </>
              )}
            </motion.button>
            
            {/* Parts list */}
            {parts.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Assembly Parts
                </div>
                <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                  {parts.map((part, i) => (
                    <motion.button
                      key={part}
                      onClick={() => onPartSelect(part)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-medium transition-all flex items-center gap-2 ${
                        selectedPart === part
                          ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'bg-secondary/30 hover:bg-secondary/50 text-foreground border border-transparent'
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      whileHover={{ x: 5 }}
                    >
                      <div className={`w-2 h-2 rounded-full ${
                        selectedPart === part 
                          ? 'bg-cyan-400' 
                          : 'bg-gradient-to-r from-cyan-500/50 to-purple-500/50'
                      }`} />
                      <span className="capitalize">{part.replace(/_/g, ' ')}</span>
                      
                      {/* Float indicator when exploded */}
                      {localAmount > 0.5 && (
                        <motion.div
                          className="ml-auto"
                          animate={{ y: [-1, 1, -1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <Sparkles className="w-3 h-3 text-purple-400" />
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Gesture hint */}
            <div className="pt-2 border-t border-border/30">
              <div className="text-[10px] text-muted-foreground text-center">
                💡 Use <span className="text-cyan-400 font-medium">two-hand spread</span> gesture to explode
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExplodePanel;
